import React from 'react';
import {
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { RequiredInfoField } from '../services/types';

export interface RequiredInfoValues {
  [label: string]: string;
}

interface RequiredInfoFieldsProps {
  fields?: RequiredInfoField[];
  values: RequiredInfoValues;
  onChange: (label: string, value: string) => void;
  errors?: Record<string, string | undefined>;
}

const BOOLEAN_OPTIONS = [
  { label: 'No', value: 'false' },
  { label: 'Yes', value: 'true' },
];

const helperTextForLabel = (label: string, min?: number): string | undefined => {
  if (label === 'Engraving Character Count') {
    const resolvedMin = typeof min === 'number' && Number.isFinite(min) ? min : 10;
    return `Enter the total characters to engrave (minimum of ${resolvedMin}).`;
  }
  if (label === 'Previous Location') {
    return 'Placement in relation to existing engraving? (Required only if previously engraved)';
  }
  return undefined;
};

const isMultiline = (label: string) => label === 'Engraving Text';
const isEngravingCharacterCount = (label: string) => label === 'Engraving Character Count';

const normalizeOptions = (field: RequiredInfoField) => {
  if (field.valueType === 'Boolean') {
    return BOOLEAN_OPTIONS;
  }
  if (Array.isArray(field.valueOptions) && field.valueOptions.length > 0) {
    return field.valueOptions.map((option) => ({ label: option, value: option }));
  }
  return undefined;
};

export const RequiredInfoFields: React.FC<RequiredInfoFieldsProps> = ({
  fields,
  values,
  onChange,
  errors,
}) => {
  if (!fields?.length) return null;

  return (
    <Stack spacing={2}>
      {fields.map((field) => {
        const { label, valueType } = field;
        const value = values[label] ?? '';
        const options = normalizeOptions(field);
        const resolvedMin =
          typeof field.minValue === 'number' && Number.isFinite(field.minValue)
            ? field.minValue
            : undefined;
        const helperText = errors?.[label] ?? helperTextForLabel(label, resolvedMin);
        const previouslyEngraved = values['Previously Engraved'] === 'true';
        const isPreviousLocation = label === 'Previous Location';

        if (isPreviousLocation && !previouslyEngraved) {
          return null;
        }

        const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
          onChange(label, event.target.value ?? '');
        };

        const textFieldProps: TextFieldProps = {
          key: label,
          label,
          value,
          onChange: handleChange,
          fullWidth: true,
          required: !(isPreviousLocation && !previouslyEngraved),
          helperText,
          error: Boolean(errors?.[label]),
        };

        if (options) {
          return (
            <TextField {...textFieldProps} select>
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        if (valueType === 'Number') {
          textFieldProps.onChange = (event) => {
            let nextValue = event.target.value ?? '';
            if (isEngravingCharacterCount(label)) {
              nextValue = nextValue.replace(/[^0-9]/g, '');
            }
            onChange(label, nextValue);
          };
          textFieldProps.type = 'number';
          const minForNumber = isEngravingCharacterCount(label)
            ? Math.max(1, resolvedMin ?? 10)
            : resolvedMin ?? 0;
          textFieldProps.inputProps = {
            min: minForNumber,
            step: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          };
        }

        if (isMultiline(label)) {
          textFieldProps.multiline = true;
          textFieldProps.minRows = 2;
        }

        return <TextField {...textFieldProps} />;
      })}
    </Stack>
  );
};

export default RequiredInfoFields;
